/* IntersectionObserver-based sentinel hook for infinite scroll.
   Attach the returned ref to a sentinel element at the bottom of the list. */

import { useEffect, useRef, type RefObject } from "react";

interface UseLoadMoreOptions {
	loadMore: () => void;
	hasMore: boolean;
}

export function useLoadMore({
	loadMore,
	hasMore,
}: UseLoadMoreOptions): RefObject<HTMLDivElement | null> {
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || !hasMore) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && hasMore) {
					loadMore();
				}
			},
			{ rootMargin: "200px" },
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [loadMore, hasMore]);

	return sentinelRef;
}
